/**
  * @class meleActorH264
  * @description
  *
  * # Purpose
  * Implements `<mele-actor-h264>` custom web element for playing H264 in <mele-channel>.
  * Muxing + MediaSource + SourceBuffer run in a dedicated Web Worker (Mp4MuxerThread);
  * the main thread owns only the <video> element, decode-error reset, and the buffer
  * snap-forward policy.
  */

import meleActor from 'meleActor'
import { NAL } from './nal'

const PLAY_DELAY_MAX = 0.4
const PLAY_DELAY_FIX = 0.2

// VideoToolbox decode errors (e.g. NSOSStatusErrorDomain -12909) leave <video> permanently in
// error state on MSE. We auto-reset on decode error, with throttling so a fundamentally
// broken stream doesn't reset in a tight loop. Same MEDIA_ERR_DECODE mechanism applies on
// Windows (DXVA / Media Foundation) and Linux (VA-API).
const DECODE_RESET_WINDOW_MS = 30_000
const DECODE_RESET_MAX_IN_WINDOW = 5
const DECODE_RESET_MIN_INTERVAL_MS = 2_000

// How often main forwards <video>.currentTime to the worker so it can clean old SourceBuffer
// data. ~1/sec is plenty for a 10s cleanup cadence.
const CURRENT_TIME_FORWARD_MS = 500

const READY_STATE = ['NOTHING(0)     ', 'METADATA(1)    ', 'CURRENT_DATA(2)', 'FUTURE_DATA(3) ', 'ENOUGH_DATA(4) ']

const bg_image = require('./loading.gif')

TimeRanges.prototype.toString = function() {
  let str = ''
  for(let i=0; i<this.length; i++) str += `${this.start(i)}-${this.end(i)} `
  return str
}

if(typeof (SourceBuffer)!=='undefined') {
  SourceBuffer.prototype.toString = function() {
    const info = {
      appendWindowStart: this.appendWindowStart,
      appendWindowEnd: this.appendWindowEnd,
      buffered: String(this.buffered),
      timestampOffset: this.timestampOffset,
      updating: this.updating,
    }
    return Object.entries(info).map(([k, v])=>k+':'+v).join(' ')
  }
}

HTMLVideoElement.prototype.toString = function() {
  const info = {
    ready: READY_STATE[this.readyState],
    seeking: this.seeking?1:0,
    paused: this.paused?1:0,
    ended: this.ended?1:0,
    error: this.error,
    currTime: this.currentTime,
    buffered: String(this.buffered),
    played: String(this.played),
    decoded: this.webkitDecodedFrameCount,
    dropped: this.webkitDroppedFrameCount,
    bytes: this.webkitVideoDecodedByteCount,
    size: `${this.videoHeight}x${this.videoHeight}`,
  }
  let str = Object.entries(info).map(([k, v])=>k+':'+v).join(' ')
  if(this.played.length>0 && this.currentTime !== this.played.end(0)) str += `CURRENT TIME ${this.currentTime} DIFFERS FROM PLAYTIME ${this.played.end(0)}`
  return str
}

const PROPERTIES = _.extend({}, {
  ets: { description: "event timestamp - 'optical' time of NAL in microseconds", type: 'integer', readonly: true },
  pts: { description: 'presentation timestamp - offset of NAL event time since first played NAL in microseconds', type: 'integer', readonly: true },
  edt: { description: 'event date time', type: 'string', readonly: true },
  videosize: { description: 'video resolution: WIDTHxHEIGHT', type: 'string', readonly: true },
  rendersize: { description: 'video render size: WIDTHxHEIGHT', type: 'string', readonly: true },
  ratio: { description: 'width/height ratio', type: 'string', readonly: true },
}, meleActor._properties)

export default class meleActorH264 extends meleActor {
  async init() {
    if(this.verbose>0) this.debug('INIT VIDEO')
    this.verbose = this.parentElement.parentElement.verbose
    this.first_ets = undefined // set by worker via {type:'first-ets'} once it accepts the first frame
    this.decodeErrorResets ??= [] // persists across reset() to throttle restart loops
    this.last_currentTime_forwarded = 0

    // <video> element — stays on main thread; worker only sees the MediaSource side
    const elVideo = this.elVideo = this.ownerDocument.createElement('video')
    elVideo.poster = bg_image
    elVideo.muted = true // needed for autoplay
    this.appendChild(elVideo)

    if(/iP(ad|hone|od).+Version\/[\d.]+.*Safari/i.test(navigator.userAgent) || (/Safari/i.test(navigator.userAgent) && ('ontouchstart' in window))) {
      // Safari on iOS has problem handling object-fit: contain
      elVideo.addEventListener('loadedmetadata', ()=>{
        elVideo.style.objectFit = 'none'
        setTimeout(()=>{
          elVideo.style.objectFit = 'contain'
        }, 100)
      })
    }

    elVideo.addEventListener('click', this.refocus.bind(this), { passive: false })

    // this.assignEventsForInfo(this.elVideo)
    this._frameInfoBound = this.frameInfo.bind(this) // cache to avoid per-frame .bind() allocations
    elVideo.requestVideoFrameCallback(this._frameInfoBound)
    window.requestAnimationFrame(this.animationInfo.bind(this))
    elVideo.onerror = ()=>{
      const err = elVideo.error
      this.error(err?.message || 'unknown video error')
      if(err?.code !== MediaError.MEDIA_ERR_DECODE) return
      if(this.resetting) return
      const now = Date.now()
      this.decodeErrorResets = this.decodeErrorResets.filter(t=>now-t < DECODE_RESET_WINDOW_MS)
      const last_reset = this.decodeErrorResets[this.decodeErrorResets.length-1]
      if(last_reset && now-last_reset < DECODE_RESET_MIN_INTERVAL_MS) {
        this.warn('decode error - auto-reset throttled')
        return
      }
      if(this.decodeErrorResets.length >= DECODE_RESET_MAX_IN_WINDOW) {
        this.error(`decode error - giving up after ${this.decodeErrorResets.length} resets in ${DECODE_RESET_WINDOW_MS} ms`)
        return
      }
      this.decodeErrorResets.push(now)
      this.warn(`decode error - soft restart (#${this.decodeErrorResets.length})`)
      this.lastFramePoster = this._captureLastFrame()
      this.softReset()
    }

    // Spawn the off-main-thread context that owns MediaSource + SourceBuffer + Mp4Muxer + NAL gating.
    // webpack 5 emits this as its own chunk via `new URL(..., import.meta.url)`.
    this.worker = new Worker(new URL('./Mp4MuxerThread.js', import.meta.url))
    this.worker.addEventListener('message', this._onWorkerMessage.bind(this))
    this.worker.addEventListener('error', (e)=>this.error('worker error: '+(e?.message || e)))

    const ready = new Promise((resolve, reject) => {
      this._workerReadyResolve = resolve
      this._workerReadyReject = reject
    })
    this.worker.postMessage({ type: 'init', config: { verbose: this.verbose } })

    let handle
    try {
      handle = (await ready).handle
    }catch(e) {
      this.error('worker init failed: '+(e?.message || e))
      throw e
    }
    elVideo.srcObject = handle
    if(this.verbose>0) this.debug('PLAY() VIDEO')
    elVideo.play().catch(e => {
      // AbortError fires when destroy()/reset() races ahead of the initial play() resolving
      // (seen on macOS where teardown can land before play() settles). Benign — the next
      // init() will call play() again. Anything else is a real error.
      if(e?.name !== 'AbortError') this.error('play() failed: '+(e?.message || e))
    })

    super.init()
  } // init

  _onWorkerMessage({ data }) {
    if(!data) return
    switch(data.type) {
      case 'ready':
        if(this._workerReadyResolve) {
          this._workerReadyResolve(data)
          this._workerReadyResolve = this._workerReadyReject = null
        }
        break
      case 'fatal':
        if(this._workerReadyReject) {
          this._workerReadyReject(new Error(data.message))
          this._workerReadyResolve = this._workerReadyReject = null
        }else{
          this.error('worker fatal: '+data.message)
        }
        break
      case 'log': {
        const fn = this[data.level]
        if(typeof fn==='function') fn.apply(this, data.args)
        break
      }
      case 'first-ets':
        this.first_ets = data.ets
        break
      case 'updateend':
        this._onWorkerUpdateend(data.buffer_end)
        break
      case 'sps-changed':
        // Worker saw a different SPS mid-stream and asked for a full reset.
        // Old single-thread code did the same (terminate state, save/restore queue).
        if(!this.resetting) this.reset()
        break
    }
  } // _onWorkerMessage

  _onWorkerUpdateend(buffer_end) {
    if(!this.elVideo) return
    if(this.elVideo.currentTime < buffer_end - PLAY_DELAY_MAX) {
      this.warn(`RESET CURRENT TIME: currentTime ${this.elVideo.currentTime} < buffer_end ${buffer_end} - PLAY_DELAY_MAX ${PLAY_DELAY_MAX}`)
      this.elVideo.currentTime = buffer_end - PLAY_DELAY_FIX
    }
  } // _onWorkerUpdateend

  async refocus() {
    try{
      const player = this.closest('mele-player')
      const channel = _.natural(this.parentElement.ch)
      await player.listener.request(player.source.replace('msysStreamer', 'msvcCameraAxis'), 'mmRefocus', { channel }, { request: 'mmSuccess', timeout: 10000 })
    }catch(e) {
      this.error('Error refocusing camera: '+e)
    }
  } // refocus

  async play() {
    await this.reset()
  } // play

  async playing() {
    await this.reset()
  }

  async destroy() {
    if(this.verbose>0) this.debug('DESTROY VIDEO')
    if(!this.elVideo) return
    // Tear down the worker. {destroy} gives it a chance to endOfStream/removeSourceBuffer
    // cleanly before we kill it; if it doesn't reply within a tick, terminate() forces it.
    if(this.worker) {
      try{ this.worker.postMessage({ type: 'destroy' }) }catch(e) {}
      try{ this.worker.terminate() }catch(e) {}
      this.worker = null
    }
    if(this._workerReadyReject) {
      this._workerReadyReject(new Error('destroyed before ready'))
      this._workerReadyResolve = this._workerReadyReject = null
    }
    try{ this.elVideo.srcObject = null }catch(e) {}
    this.elVideo.removeAttribute('src')
    this.elVideo.deleteElement()
    delete this.elVideo
  } // destroy

  reset() {
    if(this.resetting) return this.resetting
    if(this.resetted) return
    if(this.verbose>0) this.debug('RESET VIDEO')
    this.resetting = new Promise((resolve)=>{
      this.destroy()
        .finally(() => {
          this.init()
            .finally(()=>{
              delete this.resetting
              this.resetted = true
              resolve()
            })
        })
    })
    return this.resetting
  } // reset

  // Recover from MEDIA_ERR_DECODE without rebuilding <video>. Setting srcObject on the existing
  // element invokes the media-element load algorithm, which clears <video>.error as step 1 —
  // so we just swap the worker (fresh MediaSource) and the element resumes on the new source.
  // Avoids the visible flicker of removing/recreating <video> that full reset() causes.
  async softReset() {
    if(this.resetting) return this.resetting
    if(!this.elVideo) return
    if(this.verbose>0) this.debug('SOFT RESET (worker swap, keep <video>)')
    this.resetting = (async () => {
      try {
        if(this.worker) {
          try{ this.worker.postMessage({ type: 'destroy' }) }catch(e) {}
          try{ this.worker.terminate() }catch(e) {}
          this.worker = null
        }
        if(this._workerReadyReject) {
          this._workerReadyReject(new Error('soft-reset'))
          this._workerReadyResolve = this._workerReadyReject = null
        }
        this.first_ets = undefined // new MediaSource = new timeline

        this.worker = new Worker(new URL('./Mp4MuxerThread.js', import.meta.url))
        this.worker.addEventListener('message', this._onWorkerMessage.bind(this))
        this.worker.addEventListener('error', (e)=>this.error('worker error: '+(e?.message || e)))

        const ready = new Promise((resolve, reject) => {
          this._workerReadyResolve = resolve
          this._workerReadyReject = reject
        })
        this.worker.postMessage({ type: 'init', config: { verbose: this.verbose } })
        const { handle } = await ready

        // Swap the poster to the last good decoded frame (captured before this softReset).
        // Browser shows the poster during the brief MediaSource load gap and replaces it the
        // moment the new SourceBuffer produces a decoded frame. Falls back to no poster if
        // capture failed (video had no frames yet, drawImage threw, etc.).
        if(this.lastFramePoster) this.elVideo.poster = this.lastFramePoster
        else this.elVideo.removeAttribute('poster')
        this.elVideo.srcObject = handle
        this.elVideo.play().catch(e => {
          if(e?.name !== 'AbortError') this.error('soft-reset play failed: '+(e?.message || e))
        })
      } finally {
        this.resetting = null
      }
    })()
    return this.resetting
  } // softReset

  // Capture the currently-displayed video frame to a data URL. Used by softReset() so the
  // brief MediaSource load gap shows the last good frame instead of a transparent/white
  // element. Returns null when there's no frame to capture (videoWidth=0 before first play,
  // or drawImage taints/throws on a hostile source). JPEG-0.7 keeps the data URL small.
  _captureLastFrame() {
    const v = this.elVideo
    if(!v || !v.videoWidth) return null
    try {
      const canvas = this.ownerDocument.createElement('canvas')
      canvas.width = v.videoWidth
      canvas.height = v.videoHeight
      canvas.getContext('2d').drawImage(v, 0, 0)
      return canvas.toDataURL('image/jpeg', 0.7)
    }catch(e) {
      return null
    }
  } // _captureLastFrame

  async message({ event_dt, params }) {
    if(this.resetted) {
      delete this.resetted
      this.elVideo?.play().catch(e => {
        if(e?.name !== 'AbortError') this.error('play() after reset failed: '+(e?.message || e))
      })
    }
    const nal = params.payload
    if(!nal || !this.worker) return
    const ets = Math.floor(params.event_dt/1000)
    const its = Math.floor(event_dt/1000) // issued timestamp - time when NAL was published by mBox
    const lts = Math.floor(params.received_dt/1000) // load timestamp - added by mele-player on mmMessage arrival
    if(params.id>0 && this.elVideo) this.elVideo.last_frame_id = params.id
    // Copy the NAL bytes into a fresh ArrayBuffer and transfer that. We can't reuse
    // nal.buffer directly because some sources (mm-file replay) hand us multiple NAL
    // views that share a single underlying chunk buffer — transferring it would detach
    // the others. The slice() cost is one small memcpy per NAL.
    const payload = nal.slice().buffer
    try {
      this.worker.postMessage({
        type: 'nal',
        payload,
        ets,
        its,
        lts,
        id: params.id,
      }, [payload])
    }catch(e) {
      this.error('forward to worker failed: '+(e?.message || e))
    }
  } // message

  frameInfo(now, metadata) {
    if(!this.isConnected) return // we were removed from DOM
    const pts = Math.round(metadata.mediaTime*1_000)
    if(pts*1000!==this.pts) {
      this.pts = pts*1000
      const ets = this.ets = (this.first_ets + pts)*1000
      this.edt = _.usec2t(ets)
      this.parentElement.vts = ets
    }

    this.ratio = NAL.aspectRatio(metadata.width, metadata.height)

    this.parentElement.videosize = this.videosize = metadata.width+'x'+metadata.height
    const ratio = metadata.width / metadata.height
    const ratio2 = this.clientWidth / this.clientHeight
    if(ratio > ratio2) {
      this.rendersize = this.clientWidth+'x'+Math.round(this.clientWidth/ratio)
    }else{
      this.rendersize = Math.round(this.clientHeight*ratio)+'x'+this.clientHeight
    }
    this.parentElement.rendersize = this.rendersize

    // Periodically forward currentTime to the worker so it can clean old SourceBuffer data.
    if(now - this.last_currentTime_forwarded > CURRENT_TIME_FORWARD_MS) {
      this.last_currentTime_forwarded = now
      try{ this.worker?.postMessage({ type: 'currentTime', time: this.elVideo.currentTime }) }catch(e) {}
    }

    this.elVideo.requestVideoFrameCallback(this._frameInfoBound)
  } // frameInfo

  animationInfo(now) {
    if(!(this.verbose>4)) return
    const el = this.elVideo
    const info = {
      ready: READY_STATE[el.readyState],
      currTime: el.currentTime,
      buffered: String(el.buffered),
      decoded: el.webkitDecodedFrameCount,
      dropped: el.webkitDroppedFrameCount,
      bytes: el.webkitVideoDecodedByteCount,
    }
    this.debug('ANIMATION now', now, Object.entries(info).map(([k, v])=>k+':'+v).join(' '))
    window.requestAnimationFrame(this.animationInfo.bind(this))
  } // animationInfo

  // assignEventsForInfo(el) {
  //   // Listeners only feed eventInfo(), which itself early-returns unless verbose>3.
  //   // Wiring them costs CPU at init/reset and fires listener bookkeeping on every state
  //   // change of <video> (many per second during playback).
  //   if(this.verbose<=3) return
  //   let proto = el
  //   while((proto = proto.__proto__)) {
  //     for(const event of Object.keys(proto).filter(k=>/^on/.test(k))) {
  //       el.addEventListener(event.substr(2), this.eventInfo.bind(this, el), { passive: true })
  //     }
  //   }
  // } // assignEventsForInfo

  eventInfo(src, event) {
    if(!(this.verbose>3)) return
    try{
      const { type, target: el, timeStamp } = event
      const time = String(_.roundBy(timeStamp, 3)).padStart(8, ' ')
      if(src!==el && this.verbose>2) this.warn('EVENT source and target differs: ', src, event)
      const target_type = el.constructor.name.toUpperCase()
      let str = `${target_type} EVENT [${time}] ${type}:`
      str = str.padEnd(52)
      let additional_info = ''
      if(target_type==='HTMLVIDEOELEMENT') {
        additional_info += String(el)
      }else if(target_type==='SOURCEBUFFER') {
        additional_info += String(el)
      }

      this.debug(str+additional_info)
    }catch(e) {
      // sometimes this is called when source buffer is already removed
    }
  } // eventInfo
} // class meleActorH264

meleActorH264._properties = PROPERTIES
meleActorH264.observedAttributes = _.keys(PROPERTIES)
window.customElements.define('mele-actor-h264', meleActorH264)
